import { calendarSwitchSx } from '../calendar/shared/calendarSwitchStyle';
import React, { useContext, useEffect, useMemo, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Alert, Box, Button, FormControlLabel, Paper, Switch, Typography } from "@mui/material";
import { startOfDay, isSameMonth } from "date-fns";
import "../../../../../platform/assets/css/scheduledBookingType.css";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import BookingRecordModule from "./bookingRecord";
import {
  GET_BOOKING_ANSWERS,
  GET_BOOKING_BY_ID,
} from "../../../../../platform/graphql/queries";
import ErrorModule from "../../../../../platform/components/common/error";
import { useCalendar } from "../calendar/calendarContext";
import { BookingTypeStatus } from "../../../../../platform/util/utils";
import { formatInTimeZone } from "date-fns-tz";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import { UPDATE_STAFF_GOOGLE_EVENTS } from "../../../../../platform/graphql/mutations";
import { CHECK_GOOGLE_CALENDAR_INTEGRATION } from "../../../../../platform/graphql/queries";
import { GoogleCalendarService } from "../../../../../platform/services/calendar/googleCalendarService";

const BookingRecord = BookingRecordModule?.default || BookingRecordModule;
const ErrorView = ErrorModule?.default || ErrorModule;
const LEGACY_GET_BOOKINGS_BY_EMAIL = gql`
  query LegacyGetBookingsByEmail($email: String, $selectedYear: Int) {
    getBookingsByEmail(email: $email, selectedYear: $selectedYear) {
      id
      bookingId
      timezone
      time
      createdAt
      status
      inviteeName
      inviteeEmail
      inviteePhoneNumbers
      bookedByRole
      bookedByName
      bookedByEmail
      bookedForName
      bookedForEmail
      bookedForPhone
      confirmationEmails
      recurringOccurrences
      exceptionDates
      bookingTypeName
      bookingTypeColor
      staffName
      staffEmail
      staffId
      duration
      quickTitle
      quickNotes
      quickSource
    }
  }
`;

export function ScheduledBookingType() {
  const [googleConnectError, setGoogleConnectError] = useState('');
  const { providerUser } = useContext(UserContext); 
  const {
    bookingDatas,
    myBookingTypes,
    currentMonth,
    setCurrentMonth,
    googleEventsEnabled,
    setGoogleEventsEnabled,
    reloadBusinesses,
  } = useCalendar(); 
  const navigate = useNavigate();
  const location = useLocation(); // current page info
  const selectedBusiness = useSelectedBusiness();
  const staffRecord = selectedBusiness?.businessStaffs?.find(
    (staff) => Number(staff.id) === Number(providerUser?.staffId)
  );
  const searchParams = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search]
  );
  const scheduleLookupEmail = useMemo(
    () =>
      String(
        searchParams.get("email") ||
        providerUser?.email ||
        providerUser?.primaryEmail ||
        ""
      ).trim(),
    [providerUser?.email, providerUser?.primaryEmail, searchParams]
  );
  const deepLinkedBookingId = String(searchParams.get("bookingId") || "").trim();
  const deepLinkedReferralLeadId = String(searchParams.get("referralLeadId") || "").trim();
  const deepLinkedAction = String(searchParams.get("action") || "").trim().toLowerCase();
  const deepLinkedMonth = String(searchParams.get("month") || "").trim();
  const deepLinkedQueryYear = useMemo(() => {
    const parsedMonth = deepLinkedMonth ? new Date(deepLinkedMonth) : null;
    if (parsedMonth && !Number.isNaN(parsedMonth.getTime())) {
      return parsedMonth.getFullYear();
    }
    return currentMonth.getFullYear();
  }, [currentMonth, deepLinkedMonth]);
  const [pinnedDeepLinkedBooking, setPinnedDeepLinkedBooking] = useState(null);
  const [googleToggleBusy, setGoogleToggleBusy] = useState(false);
  const [, setNeedsGoogleConnect] = useState(false);
  const isGoogleToggleOn = typeof staffRecord?.showGoogleEvents === "boolean"
    ? Boolean(staffRecord.showGoogleEvents)
    : Boolean(googleEventsEnabled);



  // Query booking answers for the current provider.
  const {
    loading: answersLoading,
    error: answersError,
    data: answersData,
  } = useQuery(GET_BOOKING_ANSWERS, {
    variables: { staffId: providerUser.staffId, email: scheduleLookupEmail },
    skip: (!providerUser.staffId && !scheduleLookupEmail),
  });
  const { data: deepLinkedBookingData } = useQuery(GET_BOOKING_BY_ID, {
    variables: { id: deepLinkedBookingId },
    skip: !deepLinkedBookingId,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });
  const { data: deepLinkedBookingsByEmailData } = useQuery(LEGACY_GET_BOOKINGS_BY_EMAIL, {
    variables: {
      email: scheduleLookupEmail,
      selectedYear: deepLinkedQueryYear,
    },
    skip: !scheduleLookupEmail || (!deepLinkedBookingId && !deepLinkedReferralLeadId),
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });
  const parseReferralLeadIdFromBooking = React.useCallback((booking) => {
    const directLeadId = String(booking?.referralLeadId || "").trim();
    if (directLeadId) return directLeadId;
    const notes = String(booking?.quickNotes || "").trim();
    const match = notes.match(/(?:referral|tour\s+for)\s+lead\s*#(\d+)/i);
    return match?.[1] ? String(match[1]).trim() : "";
  }, []);
  const rawEmailBookings = useMemo(
    () => (deepLinkedBookingsByEmailData?.getBookingsByEmail ?? []).filter(
      (booking) => Number(booking?.status) !== Number(BookingTypeStatus.DELETED)
    ),
    [deepLinkedBookingsByEmailData?.getBookingsByEmail]
  );
  const deepLinkedBookingCandidate = useMemo(() => {
    if (!deepLinkedBookingId && !deepLinkedReferralLeadId) return null;
    const rawLeadMatch = deepLinkedReferralLeadId
      ? rawEmailBookings.find(
          (booking) => parseReferralLeadIdFromBooking(booking) === deepLinkedReferralLeadId
        ) || null
      : null;
    if (rawLeadMatch) return rawLeadMatch;
    const rawDirectBookingMatch = deepLinkedBookingId
      ? rawEmailBookings.find(
          (booking) => String(booking?.id || "").trim() === deepLinkedBookingId
        ) || null
      : null;
    if (rawDirectBookingMatch) return rawDirectBookingMatch;
    const activeLeadMatch = deepLinkedReferralLeadId
      ? bookingDatas.find((booking) => {
          if (Number(booking?.status) === Number(BookingTypeStatus.DELETED)) return false;
          return parseReferralLeadIdFromBooking(booking) === deepLinkedReferralLeadId;
        }) || null
      : null;
    if (activeLeadMatch) return activeLeadMatch;
    const directBookingMatch = deepLinkedBookingId
      ? bookingDatas.find(
          (booking) => String(booking?.id || "").trim() === deepLinkedBookingId
        ) || null
      : null;
    if (directBookingMatch) return directBookingMatch;
    return (
      (deepLinkedReferralLeadId &&
        parseReferralLeadIdFromBooking(deepLinkedBookingData?.booking) ===
          deepLinkedReferralLeadId
        ? deepLinkedBookingData?.booking
        : null) ||
      deepLinkedBookingData?.booking ||
      null
    );
  }, [
    rawEmailBookings,
    bookingDatas,
    deepLinkedBookingData?.booking,
    deepLinkedBookingId,
    deepLinkedReferralLeadId,
    deepLinkedQueryYear,
    parseReferralLeadIdFromBooking,
  ]);
  const { data: googleIntegrationData, refetch: refetchGoogleIntegration } = useQuery(
    CHECK_GOOGLE_CALENDAR_INTEGRATION,
    {
      variables: { staffId: providerUser?.staffId },
      skip: !providerUser?.staffId,
      fetchPolicy: "network-only",
    }
  );
  const [updateStaffGoogleEvents] = useMutation(UPDATE_STAFF_GOOGLE_EVENTS);
  const isGoogleConnected = Boolean(googleIntegrationData?.checkGoogleCalendarIntegration);

  useEffect(() => {
    if (!deepLinkedMonth) return;
    const parsedMonth = new Date(deepLinkedMonth);
    if (Number.isNaN(parsedMonth.getTime())) return;
    if (currentMonth.getFullYear() === parsedMonth.getFullYear() && currentMonth.getMonth() === parsedMonth.getMonth()) {
      return;
    }
    setCurrentMonth(parsedMonth);
  }, [currentMonth, deepLinkedMonth, setCurrentMonth]);

  useEffect(() => {
    if (isGoogleConnected) {
      setNeedsGoogleConnect(false);
    }
  }, [isGoogleConnected]);

  useEffect(() => {
    if (!deepLinkedBookingId && !deepLinkedReferralLeadId) {
      setPinnedDeepLinkedBooking(null);
      return;
    }
    if (deepLinkedBookingCandidate) {
      setPinnedDeepLinkedBooking((prev) => {
        const prevId = String(prev?.id || "").trim();
        const nextId = String(deepLinkedBookingCandidate?.id || "").trim();
        if (prevId === nextId) return prev;
        return deepLinkedBookingCandidate;
      });
    }
  }, [deepLinkedBookingCandidate, deepLinkedBookingId, deepLinkedReferralLeadId]);

  useEffect(() => {
    const onGoogleConnected = async (event) => {
      const isExpectedMessage =
        event.origin === window.location.origin &&
        event?.data?.type === "google_calendar_connected" &&
        Number(event?.data?.staffId) === Number(providerUser?.staffId);
      if (!isExpectedMessage) return;

      try {
        setGoogleToggleBusy(true);
        const res = await refetchGoogleIntegration({ staffId: providerUser?.staffId });
        if (!Boolean(res?.data?.checkGoogleCalendarIntegration)) return;

        setNeedsGoogleConnect(false);
        setGoogleEventsEnabled(true);
        await reloadBusinesses();
      } catch (error) {
        console.error("Error finalizing Google connection from My Schedule:", error);
      } finally {
        setGoogleToggleBusy(false);
      }
    };

    const onStorage = event => {
      if (event.key !== 'timelyGoogleCalendarConnected' || !event.newValue) return;
      try { onGoogleConnected({ origin: window.location.origin, data: JSON.parse(event.newValue) }); } catch (_) { /* Ignore unrelated storage. */ }
    };
    window.addEventListener("message", onGoogleConnected);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("message", onGoogleConnected);
      window.removeEventListener("storage", onStorage);
    };
  }, [
    providerUser?.staffId,
    refetchGoogleIntegration,
    reloadBusinesses,
    setGoogleEventsEnabled,
    updateStaffGoogleEvents,
  ]);

  if (!scheduleLookupEmail) {
    navigate("/login", {
      state: { from: location.pathname },
       replace: true,
    });
    return null;
  }

  if (answersLoading) return "Loading...";
  if (answersError) {
    console.error(answersError);
    return <ErrorView />;
  }

  // Helper to fetch booking type details.
  const getBookingDetail = (booking) => {
    return myBookingTypes?.find(
      (e) => Number(e.id) === Number(booking?.bookingTypeId)
    );
  };
 
  // Only filter out bookings that are marked as deleted.
  const sortedBookings = [...bookingDatas]
    .filter((booking) => {
      const source = String(booking?.source || "").toLowerCase();
      const isGoogle = source.includes("google");
      if (isGoogle) return true;
      return Number(booking?.status) !== Number(BookingTypeStatus.DELETED);
    })
    .filter((booking) => !!parseValidDate(booking.time))
    .sort((a, b) => parseValidDate(a.time) - parseValidDate(b.time));

  const currentYear = currentMonth.getFullYear();
  const now = new Date();
  const todayStart = startOfDay(now);

  // Build upcoming bookings across all future dates so newly booked items are visible
  // even when they fall outside the currently viewed month.
  const upcomingBookings = sortedBookings.filter((booking) => {
    const bookingDate = parseValidDate(booking.time);
    if (!bookingDate) return false;
    if (isSameMonth(bookingDate, now)) {
      return bookingDate >= now;
    }
    return bookingDate >= todayStart;
  });
  const deepLinkedBooking =
    deepLinkedBookingId || deepLinkedReferralLeadId
      ? deepLinkedBookingCandidate ||
        sortedBookings.find(
          (booking) => String(booking?.id || "").trim() === deepLinkedBookingId
        ) ||
        pinnedDeepLinkedBooking ||
        deepLinkedBookingData?.booking ||
        null
    : null;
  const visibleUpcomingBookings = !deepLinkedBooking
    ? upcomingBookings
    : upcomingBookings.some(
        (booking) =>
          String(booking?.id || "").trim() ===
          String(deepLinkedBooking?.id || "").trim()
      )
    ? upcomingBookings
    : [deepLinkedBooking, ...upcomingBookings];

  // Group upcoming bookings by formatted date.
  const upcomingBookingsMap = new Map();
  visibleUpcomingBookings.forEach((booking) => {
    const bookingDate = parseValidDate(booking.time);
    if (!bookingDate) return;
    const dateKey = formatInTimeZone(bookingDate, providerUser.timeZone.timezone, "EEEE, MMMM d, yyyy");
    if (!upcomingBookingsMap.has(dateKey)) {
      upcomingBookingsMap.set(dateKey, []);
    }
    upcomingBookingsMap.get(dateKey).push(booking);
  });
  const upcomingBookingsArray = Array.from(upcomingBookingsMap, ([date, bookings]) => ({
    date,
    bookings,
  }));

  const startGoogleConnect = () => {
    setGoogleConnectError('');
    try {
      const googleCalendarService = new GoogleCalendarService();
      const authUrl = googleCalendarService.getAuthUrl("", { popup: true });
      localStorage.setItem("calendarAuthPending", "true");
      const popup = window.open(authUrl, 'timely-google-calendar', 'popup=yes,width=520,height=700');
      if (!popup) window.location.assign(authUrl);
    } catch (error) {
      console.error("Error initiating Google OAuth from My Schedule:", error);
      setGoogleConnectError(error.message);
    }
  };

  const handleGoogleToggle = async () => {
    const newValue = !isGoogleToggleOn;

    if (newValue && !isGoogleConnected) {
      setNeedsGoogleConnect(true);
      setGoogleEventsEnabled(false);
      startGoogleConnect();
      return;
    }

    try {
      setGoogleToggleBusy(true);
      setGoogleEventsEnabled(newValue);
      await updateStaffGoogleEvents({
        variables: {
          staffId: providerUser?.staffId,
          showGoogleEvents: newValue,
        },
      });
      await reloadBusinesses();
    } catch (error) {
      setGoogleEventsEnabled(!newValue);
      console.error("Error updating Google toggle on My Schedule:", error);
    } finally {
      setGoogleToggleBusy(false);
    }
  };
 
  return (
    <Box>
      {googleConnectError && <Alert severity="error">{googleConnectError}</Alert>}
      <Box sx={{ mb: 0.75, display: "flex", justifyContent: "flex-end" }}>
        <Paper
          elevation={0}
          sx={{
            px: 1,
            py: 0.35,
            borderRadius: 999,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            backgroundImage: "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.primary" }}>
              Google
            </Typography>
            <FormControlLabel
              sx={{ mr: 0, ml: 0, "& .MuiFormControlLabel-label": { fontSize: 12, color: "text.secondary" } }}
              control={
                <Switch
                  size="small"
                  checked={isGoogleToggleOn}
                  onChange={handleGoogleToggle}
                  disabled={!providerUser?.staffId || googleToggleBusy}
                  inputProps={{ 'aria-label': 'Show Google Calendar events' }}
                  sx={calendarSwitchSx}
                />
              }
              label={isGoogleToggleOn ? "On" : "Off"}
            />
            {!isGoogleConnected ? (
              <Button
                size="small"
                variant="text"
                onClick={startGoogleConnect}
                sx={{ minWidth: "auto", px: 0.5, fontSize: 12, textTransform: "none" }}
              >
                Connect
              </Button>
            ) : null}
          </Box>
        </Paper>
      </Box>
      <BookingRecord
        currentYear={currentYear}
        bookingType="upcoming"
        bookingsArray={upcomingBookingsArray}
        getBookingDetail={getBookingDetail}
        answersData={answersData}
        timezone={providerUser?.timeZone?.timezone}
        compact
        initialBookingId={deepLinkedBookingId}
        initialAction={deepLinkedAction}
      />
    </Box>
  );
}

export default ScheduledBookingType;
  const parseValidDate = (value) => {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };
